/**
 * Sınav Oturumu — Spec Bölüm 7.3 + S2.T8
 *
 * Özellikler:
 *  - GET /api/public/attempts/:id/state → tüm soru listesi + verilmiş cevaplar
 *  - Geri sayım (durunca otomatik tamamla)
 *  - Soru ekranı: MC + IMG (MC_NEG / MASS / COM iskelesi)
 *  - Auto-save: her seçimden sonra POST /answer
 *  - Sekme tespiti: visibilitychange → POST /tab-switch (Spec 7.3.1)
 *  - "Şüpheli işaretle" (Spec 7.3.4) — V2'de eklenir
 *  - Crash recovery: state yeniden yüklenince responses zaten DB'de
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import {
  clearQueue,
  flushQueue,
  getQueueLength,
  submitOrQueueAnswer,
} from '../lib/offline-queue.js';

interface Question {
  id: string;
  topic: string;
  subtopic: string;
  difficulty: string;
  format: 'MC' | 'MC_NEG' | 'IMG' | 'MASS' | 'COM' | 'DRAW_ERR' | 'SEQ';
  questionText: string;
  imageUrl: string | null;
  options: Array<{ key: string; text: string }>;
  points: number;
  estimatedTimeSec: number;
}

interface ResponseRow {
  questionId: string;
  selectedOption: string | null;
  numericAnswer: number | null;
  isFlagged: boolean;
  flagNote: string | null;
  answeredAt: string;
  timeSpentSec: number;
}

interface StateResponse {
  attemptId: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED' | 'ABANDONED';
  startedAt: string;
  expiresAt: string;
  remainingSec: number;
  durationMin: number;
  totalQuestions: number;
  anonymousMode: boolean;
  candidate: { displayName: string };
  questions: Question[];
  responses: ResponseRow[];
  tabSwitchCount: number;
}

const TOPIC_LABELS: Record<string, string> = {
  PM: 'Parça Modelleme',
  AS: 'Assembly',
  DR: 'Drawing',
  SK: 'Sketch',
  SM: 'Sac Metal',
  WD: 'Weldments',
  SF: 'Surfacing',
  CF: 'Configurations',
  GN: 'Genel',
  DT: 'Teori',
};

export function ExamSessionPage(): JSX.Element {
  const params = useParams<{ attemptId: string }>();
  const attemptId = params.attemptId ?? '';
  const [, setLocation] = useLocation();

  const [state, setState] = useState<StateResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [numericAnswers, setNumericAnswers] = useState<Record<string, number>>({});
  const [savingQid, setSavingQid] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [remainingSec, setRemainingSec] = useState<number>(0);
  const [tabSwitchAlertShown, setTabSwitchAlertShown] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [queueLength, setQueueLength] = useState(0);
  const questionStartTimeRef = useRef<number>(Date.now());

  // ─── Initial load ───
  useEffect(() => {
    if (!attemptId) {
      setLoadError('Geçersiz oturum');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<StateResponse>(
          `/api/public/attempts/${attemptId}/state`,
        );
        if (cancelled) return;
        if (data.status === 'COMPLETED') {
          setLocation(`/sinav/sonuc/${attemptId}`);
          return;
        }
        if (data.status !== 'IN_PROGRESS') {
          setLoadError('Bu sınav artık aktif değil.');
          setState(data);
          return;
        }
        setState(data);
        setRemainingSec(data.remainingSec);
        // Mevcut cevapları yükle (text + numeric)
        const textMap: Record<string, string> = {};
        const numMap: Record<string, number> = {};
        for (const r of data.responses) {
          if (r.selectedOption) textMap[r.questionId] = r.selectedOption;
          if (r.numericAnswer !== null) numMap[r.questionId] = r.numericAnswer;
        }
        setAnswers(textMap);
        setNumericAnswers(numMap);
        // İlk yanıtlanmamış soruya konumlan
        const firstUnanswered = data.questions.findIndex(
          (q) => !textMap[q.id] && numMap[q.id] === undefined,
        );
        if (firstUnanswered >= 0) setCurrentIdx(firstUnanswered);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setLoadError(err.message);
        else setLoadError('Sınav yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attemptId, setLocation]);

  // ─── Geri sayım ───
  useEffect(() => {
    if (!state || state.status !== 'IN_PROGRESS') return;
    const interval = setInterval(() => {
      setRemainingSec((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [state]);

  // Süre bittiğinde otomatik tamamla
  const completeExam = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Kuyruktaki cevapları önce flush et
      await flushQueue(attemptId).catch(() => {});
      await api.post(`/api/public/attempts/${attemptId}/complete`);
      clearQueue(attemptId);
      setLocation(`/sinav/sonuc/${attemptId}`);
    } catch (err) {
      setSubmitting(false);
      if (err instanceof ApiError) setLoadError(err.message);
      else setLoadError('Sınav tamamlanırken hata oluştu');
    }
  }, [attemptId, setLocation, submitting]);

  useEffect(() => {
    if (!state || state.status !== 'IN_PROGRESS') return;
    if (remainingSec === 0) {
      void completeExam();
    }
  }, [remainingSec, state, completeExam]);

  // ─── Anti-cheat: sekme değişimi tespiti (Spec 7.3.1) ───
  useEffect(() => {
    if (!state || state.status !== 'IN_PROGRESS') return;
    function onVis(): void {
      if (document.hidden) {
        // Fire-and-forget — counter inc
        api.post(`/api/public/attempts/${attemptId}/tab-switch`).catch(() => {});
        setTabSwitchAlertShown(true);
      }
    }
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [attemptId, state]);

  // ─── Offline detection + queue flush (Spec 7.3.2) ───
  useEffect(() => {
    if (!attemptId) return;
    setQueueLength(getQueueLength(attemptId));
    async function handleOnline(): Promise<void> {
      setIsOnline(true);
      try {
        const { remaining } = await flushQueue(attemptId);
        setQueueLength(remaining);
      } catch {
        // Server error — queue zaten yönetildi
        setQueueLength(getQueueLength(attemptId));
      }
    }
    function handleOffline(): void {
      setIsOnline(false);
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // İlk yükte de kuyruğu flush et (önceki tab session kalıntısı olabilir)
    if (navigator.onLine) {
      void handleOnline();
    }
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [attemptId]);

  // ─── Soru zaman ölçümü ───
  useEffect(() => {
    questionStartTimeRef.current = Date.now();
  }, [currentIdx]);

  // ─── Cevap kaydet ───
  async function submitAnswer(questionId: string, key: string): Promise<void> {
    if (!state) return;
    setAnswers((prev) => ({ ...prev, [questionId]: key }));
    setSavingQid(questionId);
    const timeSpentSec = Math.min(
      7200,
      Math.max(1, Math.floor((Date.now() - questionStartTimeRef.current) / 1000)),
    );
    try {
      const result = await submitOrQueueAnswer(attemptId, {
        questionId,
        selectedOption: key,
        timeSpentSec,
      });
      setQueueLength(result.queueLength);
      if (result.status === 'queued') {
        setIsOnline(false);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'CONFLICT') {
          setLocation(`/sinav/sonuc/${attemptId}`);
        }
      }
    } finally {
      setSavingQid(null);
    }
  }

  async function submitNumericAnswer(
    questionId: string,
    value: number,
  ): Promise<void> {
    if (!state) return;
    setNumericAnswers((prev) => ({ ...prev, [questionId]: value }));
    setSavingQid(questionId);
    const timeSpentSec = Math.min(
      7200,
      Math.max(1, Math.floor((Date.now() - questionStartTimeRef.current) / 1000)),
    );
    try {
      const result = await submitOrQueueAnswer(attemptId, {
        questionId,
        numericAnswer: value,
        timeSpentSec,
      });
      setQueueLength(result.queueLength);
      if (result.status === 'queued') setIsOnline(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        setLocation(`/sinav/sonuc/${attemptId}`);
      }
    } finally {
      setSavingQid(null);
    }
  }

  /** DRAW_ERR: tıklanan koordinatları JSON encoded string olarak gönder */
  async function submitRegionAnswer(
    questionId: string,
    regions: Array<{ x: number; y: number }>,
  ): Promise<void> {
    if (!state) return;
    const encoded = JSON.stringify(regions);
    setAnswers((prev) => ({ ...prev, [questionId]: encoded }));
    setSavingQid(questionId);
    const timeSpentSec = Math.min(
      7200,
      Math.max(1, Math.floor((Date.now() - questionStartTimeRef.current) / 1000)),
    );
    try {
      const result = await submitOrQueueAnswer(attemptId, {
        questionId,
        selectedOption: encoded,
        timeSpentSec,
      });
      setQueueLength(result.queueLength);
      if (result.status === 'queued') setIsOnline(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        setLocation(`/sinav/sonuc/${attemptId}`);
      }
    } finally {
      setSavingQid(null);
    }
  }

  const answeredCount = useMemo(
    () =>
      Object.keys(answers).length +
      Object.keys(numericAnswers).filter((qid) => !(qid in answers)).length,
    [answers, numericAnswers],
  );

  if (loadError && !state) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-2xl px-6 py-12">
          <Alert variant="danger">{loadError}</Alert>
          <Button
            variant="secondary"
            onClick={() => setLocation('/')}
            className="mt-4"
          >
            Ana Sayfa
          </Button>
        </main>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-2xl px-6 py-12">
          <p className="text-slate-500">Sınav yükleniyor…</p>
        </main>
      </div>
    );
  }

  const q = state.questions[currentIdx];
  if (!q) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-2xl px-6 py-12">
          <Alert variant="danger">Soru bulunamadı</Alert>
        </main>
      </div>
    );
  }

  const mm = Math.floor(remainingSec / 60);
  const ss = remainingSec % 60;
  const isLast = currentIdx === state.questions.length - 1;
  const selectedKey = answers[q.id];
  const timeLow = remainingSec < 5 * 60;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Custom header — auth/logout YOK (aday session-less) */}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Aday
            </p>
            <p className="truncate text-sm font-medium text-slate-900">
              {state.candidate.displayName}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Bağlantı/sync durumu */}
            <div
              className={`hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium sm:flex ${
                !isOnline
                  ? 'bg-red-50 text-red-700'
                  : queueLength > 0
                  ? 'bg-amber-50 text-amber-800'
                  : 'bg-green-50 text-green-800'
              }`}
              title={
                !isOnline
                  ? 'Bağlantı kesik — cevaplar yerel olarak kaydediliyor'
                  : queueLength > 0
                  ? `${queueLength} cevap senkronize ediliyor`
                  : 'Tüm cevaplar senkron'
              }
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  !isOnline ? 'bg-red-500' : queueLength > 0 ? 'bg-amber-500' : 'bg-green-500'
                }`}
              />
              {!isOnline
                ? 'Çevrimdışı'
                : queueLength > 0
                ? `Sync (${queueLength})`
                : 'Senkron'}
            </div>

            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-slate-500">
                Kalan Süre
              </p>
              <p
                className={`font-mono text-2xl font-bold ${
                  timeLow ? 'text-red-600' : 'text-blue-900'
                }`}
              >
                {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        {/* Sekme uyarısı */}
        {tabSwitchAlertShown && (
          <div className="mb-4">
            <Alert variant="warning">
              <strong>Sekme değişimi tespit edildi.</strong> Sınav sırasında
              başka sekme veya pencereye geçmeniz kaydedildi. Bu durum
              yöneticiye raporlanacak. (Toplam:{' '}
              {state.tabSwitchCount + (tabSwitchAlertShown ? 1 : 0)})
              <button
                type="button"
                onClick={() => setTabSwitchAlertShown(false)}
                className="ml-3 text-xs font-medium underline"
              >
                Kapat
              </button>
            </Alert>
          </div>
        )}

        {/* Progress bar */}
        <div className="mb-3 flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700">
            Soru {currentIdx + 1} / {state.totalQuestions}
          </span>
          <span className="text-slate-500">
            {answeredCount} / {state.totalQuestions} yanıtlandı
            {savingQid && (
              <span className="ml-2 text-xs text-amber-600">Kaydediliyor…</span>
            )}
          </span>
        </div>
        <div className="mb-6 h-2 rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-blue-900 transition-all"
            style={{
              width: `${((currentIdx + 1) / state.totalQuestions) * 100}%`,
            }}
          />
        </div>

        <Card>
          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-900">
              {TOPIC_LABELS[q.topic] ?? q.topic}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              {q.difficulty}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              {q.points} puan
            </span>
            {q.format === 'MC_NEG' && (
              <span className="rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-900">
                Olumsuz Soru
              </span>
            )}
          </div>

          <h2 className="text-lg font-semibold text-slate-900">
            {q.questionText}
          </h2>

          {q.imageUrl && (
            <img
              src={q.imageUrl}
              alt="Soru görseli"
              className="mt-4 max-h-[60vh] rounded-lg border border-slate-200"
            />
          )}

          {/* MC / IMG / MC_NEG → multiple choice */}
          {['MC', 'MC_NEG', 'IMG'].includes(q.format) && (
            <div className="mt-6 space-y-2">
              {q.options.map((opt) => {
                const isSelected = opt.key === selectedKey;
                return (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => submitAnswer(q.id, opt.key)}
                    disabled={savingQid === q.id}
                    className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                      isSelected
                        ? 'border-blue-900 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-blue-400 hover:bg-slate-50'
                    } disabled:opacity-60`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        isSelected
                          ? 'bg-blue-900 text-white'
                          : 'border border-slate-300 text-slate-600'
                      }`}
                    >
                      {opt.key}
                    </span>
                    <span className="text-slate-800">{opt.text}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* MASS / COM → numeric input (Spec 5.5.1 near-miss) */}
          {(q.format === 'MASS' || q.format === 'COM') && (
            <NumericAnswer
              questionId={q.id}
              format={q.format}
              currentValue={numericAnswers[q.id]}
              onSubmit={submitNumericAnswer}
              disabled={savingQid === q.id}
            />
          )}

          {/* DRAW_ERR → click-to-tag (Spec 5.1 DRAW_ERR format) */}
          {q.format === 'DRAW_ERR' && q.imageUrl && (
            <DrawingErrorTag
              questionId={q.id}
              imageUrl={q.imageUrl}
              currentRegions={parseRegions(answers[q.id])}
              onSubmit={submitRegionAnswer}
              disabled={savingQid === q.id}
            />
          )}

          {q.format === 'DRAW_ERR' && !q.imageUrl && (
            <div className="mt-6">
              <Alert variant="warning">
                Bu soru için görsel yüklenmemiş. Yöneticiye bildirin.
              </Alert>
            </div>
          )}

          {q.format === 'SEQ' && (
            <div className="mt-6">
              <Alert variant="info">
                Feature Sequencing (SEQ) soruları V2'de aktive olacak (Spec 5.1).
                Şu an bu soruyu geçebilirsiniz.
              </Alert>
            </div>
          )}
        </Card>

        {/* Navigation */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
            disabled={currentIdx === 0}
          >
            ← Önceki
          </Button>

          <QuestionGrid
            total={state.questions.length}
            answered={answers}
            numericAnswered={numericAnswers}
            questions={state.questions}
            currentIdx={currentIdx}
            onJump={setCurrentIdx}
          />

          {!isLast ? (
            <Button
              onClick={() => setCurrentIdx(Math.min(state.questions.length - 1, currentIdx + 1))}
            >
              Sonraki →
            </Button>
          ) : (
            <Button
              onClick={() => {
                if (answeredCount < state.totalQuestions) {
                  if (
                    !confirm(
                      `${state.totalQuestions - answeredCount} soruyu yanıtlamadınız. Yine de tamamlamak istiyor musunuz? Yanıtlanmayan sorular 0 puan alır.`,
                    )
                  ) {
                    return;
                  }
                }
                void completeExam();
              }}
              loading={submitting}
            >
              Sınavı Bitir
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

/** Yan menü yerine inline mini grid — sınav esnasında soru atlanabilmesi için */
function QuestionGrid({
  total,
  answered,
  numericAnswered,
  questions,
  currentIdx,
  onJump,
}: {
  total: number;
  answered: Record<string, string>;
  numericAnswered: Record<string, number>;
  questions: Question[];
  currentIdx: number;
  onJump: (idx: number) => void;
}): JSX.Element {
  return (
    <div className="hidden flex-wrap items-center gap-1 sm:flex">
      {Array.from({ length: total }, (_, i) => {
        const q = questions[i];
        if (!q) return null;
        const isAnswered = !!answered[q.id] || numericAnswered[q.id] !== undefined;
        const isCurrent = i === currentIdx;
        return (
          <button
            key={q.id}
            type="button"
            onClick={() => onJump(i)}
            aria-label={`Soru ${i + 1}`}
            className={`h-7 w-7 rounded text-xs font-medium transition-colors ${
              isCurrent
                ? 'bg-blue-900 text-white'
                : isAnswered
                ? 'bg-green-100 text-green-900 hover:bg-green-200'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {i + 1}
          </button>
        );
      })}
    </div>
  );
}

// ─── MASS/COM Numeric Answer (Spec 5.5.1) ─────────────────────────

function NumericAnswer({
  questionId,
  format,
  currentValue,
  onSubmit,
  disabled,
}: {
  questionId: string;
  format: 'MASS' | 'COM';
  currentValue: number | undefined;
  onSubmit: (qid: string, value: number) => void;
  disabled: boolean;
}): JSX.Element {
  const [raw, setRaw] = useState<string>(
    currentValue !== undefined ? String(currentValue) : '',
  );

  // Soru değişince input'u senkronla
  useEffect(() => {
    setRaw(currentValue !== undefined ? String(currentValue) : '');
  }, [questionId, currentValue]);

  function commit(): void {
    const n = Number(raw.replace(',', '.'));
    if (!Number.isFinite(n)) return;
    if (n === currentValue) return;
    onSubmit(questionId, n);
  }

  return (
    <div className="mt-6">
      <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4">
        <p className="text-sm text-slate-700">
          {format === 'MASS'
            ? 'Parçayı modelleyip hesapladığınız kütleyi girin. SolidWorks Tools → Evaluate → Mass Properties sonucunu kullanın.'
            : 'Assembly’nin kütle merkezini (CoG) modelleyip hesaplayın. X koordinatını girin.'}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Spec 5.5.1: ≤%1 hata tam puan, %1-3 yarım, %3-10 çeyrek puan
          (near-miss).
        </p>
      </div>

      <div className="mt-4 flex items-stretch gap-2">
        <input
          type="text"
          inputMode="decimal"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder={format === 'MASS' ? '0.123' : '12.45'}
          disabled={disabled}
          className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-lg font-mono focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
          aria-label="Sayısal cevap"
        />
        <span className="flex items-center px-2 text-sm font-medium text-slate-600">
          {format === 'MASS' ? 'kg' : 'mm'}
        </span>
      </div>

      <p className="mt-2 text-xs text-slate-500">
        Cevabınız <kbd>Enter</kbd> veya alandan çıktığınızda kaydedilir.
        Virgül veya nokta kullanabilirsiniz.
      </p>
    </div>
  );
}

// ─── DRAW_ERR: Drawing Click-to-Tag (Spec 5.1) ────────────────────

function parseRegions(encoded?: string): Array<{ x: number; y: number }> {
  if (!encoded) return [];
  try {
    const parsed = JSON.parse(encoded) as unknown;
    if (
      Array.isArray(parsed) &&
      parsed.every(
        (p) =>
          typeof p === 'object' &&
          p !== null &&
          typeof (p as { x?: unknown }).x === 'number' &&
          typeof (p as { y?: unknown }).y === 'number',
      )
    ) {
      return parsed as Array<{ x: number; y: number }>;
    }
  } catch {
    // ignore
  }
  return [];
}

function DrawingErrorTag({
  questionId,
  imageUrl,
  currentRegions,
  onSubmit,
  disabled,
}: {
  questionId: string;
  imageUrl: string;
  currentRegions: Array<{ x: number; y: number }>;
  onSubmit: (qid: string, regions: Array<{ x: number; y: number }>) => void;
  disabled: boolean;
}): JSX.Element {
  const [regions, setRegions] = useState(currentRegions);

  useEffect(() => {
    setRegions(currentRegions);
  }, [questionId, currentRegions]);

  function handleClick(e: React.MouseEvent<HTMLDivElement>): void {
    if (disabled) return;
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const next = [...regions, { x, y }];
    setRegions(next);
    onSubmit(questionId, next);
  }

  function removeRegion(idx: number): void {
    const next = regions.filter((_, i) => i !== idx);
    setRegions(next);
    onSubmit(questionId, next);
  }

  function clearAll(): void {
    setRegions([]);
    onSubmit(questionId, []);
  }

  return (
    <div className="mt-6">
      <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4 text-sm text-slate-700">
        <p>
          <strong>Çizim üzerindeki tüm hataları tıklayarak işaretleyin.</strong>{' '}
          Her hata için ayrı bir pin koymalısınız. Yanlış işaretlerseniz pin'e
          tekrar tıklayarak kaldırabilirsiniz.
        </p>
      </div>

      <div
        onClick={handleClick}
        className={`relative mt-4 inline-block w-full overflow-hidden rounded-lg border-2 border-slate-300 ${
          disabled ? 'cursor-wait opacity-60' : 'cursor-crosshair'
        }`}
      >
        <img
          src={imageUrl}
          alt="Drawing hatası tespiti"
          className="block w-full select-none"
          draggable={false}
        />
        {regions.map((r, i) => (
          <button
            type="button"
            key={i}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) removeRegion(i);
            }}
            className="absolute z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white shadow-md hover:bg-red-700"
            style={{ left: `${r.x * 100}%`, top: `${r.y * 100}%` }}
            aria-label={`Hata #${i + 1}'i kaldır`}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between text-sm">
        <span className="text-slate-700">
          <strong>{regions.length}</strong> hata işaretlendi
        </span>
        {regions.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled}
            className="text-sm font-medium text-red-700 hover:underline disabled:opacity-50"
          >
            Tümünü temizle
          </button>
        )}
      </div>
    </div>
  );
}
